// src/features/profile/context/ProfileContext.jsx
// Thin context wrapper around profile services

import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../../auth/context/AuthContext';
import * as profileService from '../services/profileService';
import * as hexagramService from '../services/hexagramService';
import logger from '@/shared/utils/core/logger';
import { setPostHogUserProperties } from '@/shared/utils/analytics/posthog';


const ProfileContext = createContext({
  profile: null,
  loading: true,
  saveProfile: async () => {},
  hexagramProfile: null,
  dailyHexagram: null,
  getPersonalizedPrompt: () => '',
  getAIContext: () => null,
  refreshDailyHexagram: () => {}
});

export function ProfileProvider({ children }) {
  const { user: authUser, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hexagramProfile, setHexagramProfile] = useState(null);
  const [dailyHexagram, setDailyHexagram] = useState(null);
  // Tracks the last birth data key for which we wrote to Firestore,
  // so we skip redundant saveComprehensiveProfile calls on every session start.
  const lastSavedBirthKeyRef = useRef(null);

  // Calculate hexagrams from profile data.
  // getIsCancelled: callback returning true when the triggering effect has cleaned up.
  // Without this, fast account switching or logout while calculation is in-flight
  // would write the previous user's hexagram data to Firestore and set stale state.
  const calculateAndSetHexagrams = useCallback(async (profileData, getIsCancelled = () => false) => {
    try {
      const hexProfile = await hexagramService.calculateCompleteHexagramProfile(profileData);
      if (getIsCancelled()) return;

      if (hexProfile) {
        setHexagramProfile(hexProfile);
        logger.debug('✅ [Profile] Hexagram profile set in state');

        // Skip the Firestore write if birth data hasn't changed since the last save this session.
        // Without this guard, saveComprehensiveProfile fires on every app load even when
        // birth data is identical, producing unnecessary writes and listener noise on other devices.
        const birthKey = [profileData.birthDate, profileData.birthTime, profileData.birthPlace].join('|');
        if (lastSavedBirthKeyRef.current !== birthKey) {
          await profileService.saveComprehensiveProfile(
            profileData.uid || authUser?.uid,
            hexProfile,
            profileData
          );
          lastSavedBirthKeyRef.current = birthKey;
        } else {
          logger.debug('✅ [Profile] Birth data unchanged — skipping saveComprehensiveProfile');
        }
        if (getIsCancelled()) return;

        const daily = hexagramService.calculateDailyHexagram(
          profileData.birthDate,
          profileData.birthTime
        );

        if (!getIsCancelled() && daily) {
          setDailyHexagram(daily);
          logger.debug('✅ [Profile] Daily hexagram set in state:', daily);
        } else if (!daily) {
          logger.error('❌ [Profile] No daily hexagram calculated');
        }
      } else {
        logger.error('❌ [Profile] No hexagram profile calculated');
      }
    } catch (error) {
      logger.error('❌ [Profile] Error calculating hexagram profile:', error);
      // Non-fatal: swallowed here so callers can distinguish profile-save failure
      // from hexagram-calc failure. The user's profile data was already saved.
    }
  }, [authUser?.uid]);

  // Load profile on mount or auth change
  useEffect(() => {
    let cancelled = false;

    (async () => {
      // Wait for auth to finish loading
      if (authLoading) {
        logger.debug('⏳ [Profile] Waiting for auth to load...');
        return;
      }

      if (!authUser) {
        if (import.meta.env.DEV) {
          logger.debug('⚠️ [Profile] No authenticated user');
        }
        if (!cancelled) {
          setProfile(null);
          setHexagramProfile(null);
          setDailyHexagram(null);
          setLoading(false);
        }
        return;
      }

      if (!cancelled) setLoading(true);

      try {
        const profileData = await profileService.loadProfile(authUser.uid);
        if (cancelled) return;

        setProfile(profileData);

        if (profileData?.birthDate) {
          await calculateAndSetHexagrams(profileData, () => cancelled);
        } else {
          logger.debug('⚠️ [Profile] No birth date found in profile');
        }
      } catch (error) {
        logger.error('❌ [Profile] Error loading profile:', error);
      }

      if (!cancelled) setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [authUser?.uid, authLoading, calculateAndSetHexagrams]);

  // ── Sync profile data to PostHog user properties ──────────────────────────
  useEffect(() => {
    if (loading || !profile || !authUser) return;

    const daysSinceSignup = authUser.metadata?.creationTime
      ? Math.floor((Date.now() - new Date(authUser.metadata.creationTime).getTime()) / 86400000)
      : null;

    setPostHogUserProperties({
      onboarding_completed: profile.onboardingCompleted ?? false,
      has_birth_data: !!profile.birthDate,
      hexagram_number: dailyHexagram?.number ?? null,
      days_since_signup: daysSinceSignup,
    });
  }, [loading, profile?.onboardingCompleted, profile?.birthDate, dailyHexagram?.number, authUser?.uid]);

  // Save profile with automatic hexagram recalculation.
  // Profile save and hexagram recalculation are treated as separate concerns:
  // a hexagram calc failure (calculateAndSetHexagrams catches its own errors)
  // does not mask a successful profile write to Firestore.
  const saveProfile = useCallback(async (data) => {
    if (!authUser) {
      logger.error('❌ Cannot save profile: Not authenticated');
      throw new Error('Not authenticated');
    }

    // Save to Firestore — throws on failure so callers can show the right error
    await profileService.saveProfile(authUser.uid, data);

    const newProfile = { ...profile, ...data };
    setProfile(newProfile);

    // Recalculate hexagrams if birth data changed (support both flat and nested formats).
    // Pass () => false — explicit user saves are never cancelled mid-flight.
    if (data.birthDate || data.birthTime || data.birthPlace ||
        data.birthData?.date || data.birthData?.time || data.birthData?.place) {
      await calculateAndSetHexagrams(newProfile, () => false);
    }

    return newProfile;
  }, [authUser, profile, calculateAndSetHexagrams]);

  // Get personalized journal prompt based on daily hexagram
  const getPersonalizedPrompt = useCallback((context = 'general') => {
    return hexagramService.getPersonalizedJournalPrompt(dailyHexagram, context);
  }, [dailyHexagram]);

  // Get AI context for personalized feedback
  const getAIContext = useCallback((journalContent = '') => {
    return hexagramService.getAIContext(hexagramProfile, journalContent);
  }, [hexagramProfile]);

  // Refresh daily hexagram
  const refreshDailyHexagram = useCallback(() => {
    if (profile?.birthDate) {
      const daily = hexagramService.calculateDailyHexagram(
        profile.birthDate,
        profile.birthTime
      );
      setDailyHexagram(daily);
    }
  }, [profile?.birthDate, profile?.birthTime]);

  const contextValue = useMemo(() => ({
    profile,
    loading,
    saveProfile,
    hexagramProfile,
    dailyHexagram,
    getPersonalizedPrompt,
    getAIContext,
    refreshDailyHexagram,
  }), [profile, loading, saveProfile, hexagramProfile, dailyHexagram, getPersonalizedPrompt, getAIContext, refreshDailyHexagram]);

  return <ProfileContext.Provider value={contextValue}>{children}</ProfileContext.Provider>;
}

export function useProfile() {
  return useContext(ProfileContext);
}
